// UnifiedDetailedIncomeStatementPercent.tsx
import * as React from 'react';
import { ChevronDownSquare, ChevronUpSquare } from 'lucide-react';
import { AccountEntry, CategoryTemplate, Project, ViewType } from '../../types';
import { ViewToggle } from '../ui/ViewToggle';
import { SectionHeader, CategoryRow, SectionTotal } from './ComponentsPercentSales';
import {
  getQuarter,
  getYear,
  getYearMonthRange,
  monthMap,
  compareMonthYear as compareMonthYearHelper,
  calculateTTM,
  calculateTTMForPeriodType
} from '../../lib/financial/calculations';
import { FrontendPLCalculationService } from '../../lib/financial/frontendPLCalculations';
import { getAllFinancialReports } from '../../lib/data/financialReportsHelper';
import { getDateForPeriod } from '../../lib/utils/dateMapping';
import { EBITDABridgePercent } from './EBITDABridgePercent';

interface UnifiedDetailedIncomeStatementPercentProps {
  accounts: AccountEntry[];
  activeTemplate?: CategoryTemplate | null;
  selectedProject?: Project | null;
}


interface ExpandedState {
  sections: Record<string, boolean>;
  categories: Record<string, boolean>;
}

interface PLLineItem {
  type: 'section_header' | 'category_header' | 'account' | 'category_total' | 'section_total' | 'calculated_total' | 'calculated_percentage';
  name: string;
  values?: number[];
  level: number;
  category_value?: string;
  account_number?: string;
  is_final?: boolean;
}

interface PLDetailData {
  periods: string[];
  line_items: PLLineItem[];
}

const UnifiedDetailedIncomeStatementPercent: React.FC<UnifiedDetailedIncomeStatementPercentProps> = ({
  accounts,
  activeTemplate,
  selectedProject
}) => {
  const [viewType, setViewType] = React.useState<ViewType>('monthly');
  const [expandedState, setExpandedState] = React.useState<ExpandedState>({
    sections: {
      'Revenue': true,
      'Sales Discounts and Returns': true,
      'Cost of Goods Sold': true,
      'Operating Expenses': true,
      'Other Expenses': true
    },
    categories: {}
  });
  const [plDetailData, setPLDetailData] = React.useState<PLDetailData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [pendingExpandAll, setPendingExpandAll] = React.useState(false);

  // Frontend calculation mode - use frontend calculations by default
  const [calculationSource, setCalculationSource] = React.useState<'frontend' | 'backend'>('frontend');

  // Generate frontend P&L data
  const frontendPLData = React.useMemo(() => {
    if (calculationSource === 'frontend' && accounts.length > 0 && activeTemplate) {
      try {
        return FrontendPLCalculationService.buildPLData(accounts, activeTemplate, viewType, selectedProject);
      } catch (err) {
        console.error('Error generating frontend P&L data for Percent view:', err);
        return null;
      }
    }
    return null;
  }, [calculationSource, accounts, activeTemplate, viewType, selectedProject]);

  // Load P&L data from backend (only when using backend calculations or as fallback)
  React.useEffect(() => {
    const loadPLData = async () => {
      // Only load backend data if using backend calculations or if frontend data is not available
      if (!selectedProject?.id || (calculationSource === 'frontend' && frontendPLData)) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const reports = await getAllFinancialReports(selectedProject.id);

        if (!reports.pnl_detail?.data) {
          // If frontend data is available, don't show error
          if (calculationSource === 'frontend' && frontendPLData) {
            setLoading(false);
            return;
          }
          setError('P&L detail report not found. Please generate reports first.');
          return;
        }

        setPLDetailData(reports.pnl_detail.data);

      } catch (err) {
        console.error('Error loading P&L data:', err);
        // If frontend data is available, don't show error
        if (calculationSource === 'frontend' && frontendPLData) {
          setLoading(false);
          return;
        }
        setError('Failed to load P&L data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadPLData();
  }, [selectedProject?.id, calculationSource, frontendPLData]);

  // Get data source based on calculation mode
  const currentDataSource = calculationSource === 'frontend' && frontendPLData
    ? frontendPLData
    : plDetailData;

  // Auto-expand all after data loads if pendingExpandAll is set
  React.useEffect(() => {
    if (pendingExpandAll && currentDataSource) {
      expandAll();
    }
  }, [pendingExpandAll, currentDataSource]);

  // Auto-select appropriate view based on project period type
  React.useEffect(() => {
    if (selectedProject?.period_type) {
      const periodType = selectedProject.period_type;
      const availableViews: ViewType[] = 
        periodType === 'quarterly' ? ['quarterly', 'yearly'] :
        periodType === 'annual' ? ['yearly'] :
        ['monthly', 'quarterly', 'yearly'];
      
      // If current view is not available for this period type, switch to first available
      if (!availableViews.includes(viewType)) {
        setViewType(availableViews[0]);
      }
    }
  }, [selectedProject?.period_type, viewType]);

  // Set all categories collapsed on data load
  React.useEffect(() => {
    if (currentDataSource) {
      const newCategories: Record<string, boolean> = {};
      currentDataSource.line_items
        .filter(item => item.type === 'category_header' && item.category_value)
        .forEach(item => {
          if (item.category_value) {
            newCategories[item.category_value] = false;
          }
        });
      setExpandedState(prev => ({
        ...prev,
        categories: newCategories
      }));
    }
  }, [currentDataSource]);

  // Transform monthly data to quarterly/yearly based on view type
  const transformDataForView = React.useCallback((data: PLDetailData | null): PLDetailData | null => {
    if (!data) return null;
    if (viewType === 'monthly') {
      return data;
    }
    const monthlyPeriods = data.periods;
    const allMonths = [...monthlyPeriods].sort(compareMonthYearHelper);
    const targetPeriods = viewType === 'quarterly'
      ? [...new Set(allMonths.map(getQuarter))].sort((a, b) => {
          const [q1, y1] = a.split("'");
          const [q2, y2] = b.split("'");
          return y1 === y2 
            ? parseInt(q1.slice(1)) - parseInt(q2.slice(1)) 
            : parseInt(y1) - parseInt(y2);
        })
      : [...new Set(allMonths.map(getYear))].sort();
    const getMonthsForPeriod = (period: string): string[] => {
      if (viewType === 'quarterly') {
        const [q, year] = period.split("'");
        const quarterNum = parseInt(q.slice(1));
        const expectedMonths: string[] = [];
        const startMonth = (quarterNum - 1) * 3;
        for (let i = 0; i < 3; i++) {
          const monthNum = startMonth + i;
          const month = Object.keys(monthMap)[monthNum];
          expectedMonths.push(`${month}-${year}`);
        }
        return allMonths.filter(month => expectedMonths.includes(month));
      } else {
        return allMonths.filter(month => getYear(month) === period);
      }
    };
    const transformedLineItems: PLLineItem[] = data.line_items.map(item => {
      if (!item.values || item.type === 'section_header' || item.type === 'category_header') {
        return item;
      }
      const newValues: number[] = targetPeriods.map(period => {
        const monthsInPeriod = getMonthsForPeriod(period);
        return monthsInPeriod.reduce((sum, month) => {
          const monthIndex = monthlyPeriods.indexOf(month);
          if (monthIndex !== -1 && item.values) {
            return sum + (item.values[monthIndex] || 0);
          }
          return sum;
        }, 0);
      });
      return {
        ...item,
        values: newValues
      };
    });
    return {
      periods: targetPeriods,
      line_items: transformedLineItems
    };
  }, [viewType]);

  const transformedData = transformDataForView(currentDataSource);

  // Helper: get Net Revenue values for all periods
  const getNetRevenueValues = (data: PLDetailData | null): number[] => {
    if (!data) return [];
    const netRevenueItem = data.line_items.find(item => item.name === 'Net Revenue');
    return netRevenueItem?.values || [];
  };
  const netRevenueValues = getNetRevenueValues(transformedData);

  // Helper to get value as percent of Net Revenue
  const getPercentValue = (value: number | null | undefined, periodIdx: number) => {
    const netRevenue = netRevenueValues[periodIdx] || 0;
    if (netRevenue === 0 || value === null || value === undefined) return null;
    return ((value / netRevenue) * 100).toFixed(1);
  };

  // Helper: get period month ranges for display
  const getPeriodMonthRanges = () => {
    if (!currentDataSource || viewType === 'monthly') return {};
    const allMonths = [...currentDataSource.periods].sort(compareMonthYearHelper);
    const periods = transformedData?.periods || [];
    return periods.reduce((acc, period) => {
      if (viewType === 'yearly') {
        acc[period] = getYearMonthRange(period, allMonths);
      } else if (viewType === 'quarterly') {
        const [q, year] = period.split("'");
        const quarterNum = parseInt(q.slice(1));
        const expectedMonths: string[] = [];
        const startMonth = (quarterNum - 1) * 3;
        for (let i = 0; i < 3; i++) {
          const monthNum = startMonth + i;
          const month = Object.keys(monthMap)[monthNum];
          expectedMonths.push(`${month}-${year}`);
        }
        const quarterMonths = allMonths.filter(month => expectedMonths.includes(month));
        if (quarterMonths.length === 3) {
          acc[period] = null;
        } else {
          const firstMonth = quarterMonths[0]?.split('-')[0] || '';
          const lastMonth = quarterMonths[quarterMonths.length - 1]?.split('-')[0] || '';
          acc[period] = firstMonth === lastMonth ? firstMonth : `${firstMonth}-${lastMonth}`;
        }
      }
      return acc;
    }, {} as Record<string, string | null>);
  };
  const periodMonthRanges = getPeriodMonthRanges();

  // Section/Category structure for rendering
  const getSectionCategories = (sectionTitle: string) => {
    if (!currentDataSource) return [];
    let foundSection = false;
    return currentDataSource.line_items
      .filter(item => {
        if (item.type === 'section_header' && item.name === sectionTitle) {
          foundSection = true;
          return false;
        }
        if (item.type === 'section_header' && foundSection) {
          foundSection = false;
        }
        return foundSection && item.type === 'category_header' && item.category_value;
      })
      .map(item => item.category_value!);
  };
  const sections = [
    { title: 'Revenue', name: 'Revenue', isRevenue: true, isContraRevenue: false, categories: getSectionCategories('Revenue'), showTotal: true },
    { title: 'Sales Discounts and Returns', name: 'Sales Discounts and Returns', isRevenue: true, isContraRevenue: true, categories: getSectionCategories('Sales Discounts and Returns'), showTotal: true },
    { title: 'Cost of Goods Sold', name: 'Cost of Goods Sold', isRevenue: false, isContraRevenue: false, categories: getSectionCategories('Cost of Goods Sold'), showTotal: true },
    { title: 'Operating Expenses', name: 'Operating Expenses', isRevenue: false, isContraRevenue: false, categories: getSectionCategories('Operating Expenses'), showTotal: true },
    { title: 'Other Expenses', name: 'Other Expenses', isRevenue: false, isContraRevenue: false, categories: getSectionCategories('Other Expenses'), showTotal: true }
  ];

  // Expand/collapse logic
  const expandAll = () => {
    if (!currentDataSource) {
      setPendingExpandAll(true);
      return;
    }
    const newState: ExpandedState = {
      sections: {},
      categories: {}
    };
    Object.keys(expandedState.sections).forEach(section => {
      newState.sections[section] = true;
    });
    currentDataSource.line_items
      .filter(item => item.type === 'category_header' && item.category_value)
      .forEach(item => {
        if (item.category_value) {
          newState.categories[item.category_value] = true;
        }
      });
    setExpandedState(newState);
    setPendingExpandAll(false);
  };
  const collapseAll = () => {
    if (!currentDataSource) return;
    const newCategories: Record<string, boolean> = {};
    currentDataSource.line_items
      .filter(item => item.type === 'category_header' && item.category_value)
      .forEach(item => {
        if (item.category_value) {
          newCategories[item.category_value] = false;
        }
      });
    const newState: ExpandedState = {
      sections: { ...expandedState.sections },
      categories: newCategories
    };
    setExpandedState(newState);
  };
  const toggleSection = (section: string) => {
    setExpandedState(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [section]: !prev.sections[section]
      }
    }));
  };
  const toggleCategory = (category: string) => {
    setExpandedState(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: !prev.categories[category]
      }
    }));
  };

  // Helper: get TTM period info for header
  const getTTMPeriodInfo = () => {
    if (!currentDataSource || viewType !== 'yearly') return { label: 'TTM', range: null };
    
    const projectPeriodType = selectedProject?.period_type || 'monthly';
    const allPeriods = [...transformedData?.periods || []].sort();
    
    let lookbackPeriods: string[];
    switch (projectPeriodType) {
      case 'quarterly':
        // For quarterly data, look back 4 quarters (1 year)
        lookbackPeriods = allPeriods.slice(-4);
        break;
      case 'annual':
        // For annual data, TTM should be the most recent year (closest approximation to trailing twelve months)
        lookbackPeriods = allPeriods.slice(-1);
        break;
      default: // monthly
        // For monthly data, look back 12 months (1 year)
        lookbackPeriods = allPeriods.slice(-12);
        break;
    }
    
    if (lookbackPeriods.length === 0) return { label: 'TTM', range: null };
    
    if (projectPeriodType === 'annual' && lookbackPeriods.length > 1) {
      // For annual data with multiple years, show the range
      const oldestYear = lookbackPeriods[0];
      const newestYear = lookbackPeriods[lookbackPeriods.length - 1];
      return {
        label: 'TTM',
        range: oldestYear === newestYear ? null : `${oldestYear}-${newestYear}`
      };
    } else if (projectPeriodType === 'monthly') {
      // For monthly data, check if it's a partial year
      const allMonths = [...currentDataSource.periods].sort(compareMonthYearHelper);
      const last12Months = allMonths.slice(-12);
      if (last12Months.length === 0) return { label: 'TTM', range: null };
      const oldestMonth = last12Months[0].split('-')[0];
      const newestMonth = last12Months[last12Months.length - 1].split('-')[0];
      const isPartialYear = oldestMonth !== 'Jan' || newestMonth !== 'Dec';
      return {
        label: 'TTM',
        range: isPartialYear ? `${oldestMonth}-${newestMonth}` : null
      };
    } else {
      // For quarterly or single period, no range needed
      return {
        label: 'TTM',
        range: null
      };
    }
  };
  const ttmInfo = getTTMPeriodInfo();

  // Debug logging for calculation source
  React.useEffect(() => {
    console.log('[P&L Percent Debug] Calculation source:', calculationSource);
    console.log('[P&L Percent Debug] Frontend data available:', !!frontendPLData);
    console.log('[P&L Percent Debug] Backend data available:', !!plDetailData);
    console.log('[P&L Percent Debug] Current data source:', currentDataSource === frontendPLData ? 'frontend' : 'backend');
  }, [calculationSource, frontendPLData, plDetailData, currentDataSource]);

  // UI states
  if (!activeTemplate) {
    return (
      <div className="p-8 text-center">
        <p className="text-lg text-gray-600">
          Please select an active template to view the detailed income statement.
        </p>
      </div>
    );
  }
  if (loading && !currentDataSource) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading P&L data...</p>
      </div>
    );
  }
  if (error && !currentDataSource) {
    return (
      <div className="p-8 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">{error}</p>
        </div>
      </div>
    );
  }
  if (!transformedData) {
    return (
      <div className="p-8 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">No P&L data available.</p>
        </div>
      </div>
    );
  }

  const periods = transformedData.periods;

  return (
    <div className="overflow-hidden">
      <style>{`
        .financial-table {
          table-layout: auto;
        }
        
        .financial-table th:first-child,
        .financial-table td:first-child {
          width: 240px;
          min-width: 240px;
          max-width: 240px;
        }

        /* Account number column */
        .financial-table th:nth-child(2) {
          width: auto;
          min-width: 120px;
          max-width: 140px;
          white-space: normal;
          word-break: break-word;
          text-align: center;
        }
        .financial-table td:nth-child(2) {
          width: auto;
          min-width: 120px;
          max-width: 140px;
          white-space: nowrap;
          text-align: center;
        }
        
        .financial-table th:not(:first-child):not(:nth-child(2)),
        .financial-table td:not(:first-child):not(:nth-child(2)) {
          width: auto;
          min-width: 160px;
          max-width: 200px;
        }
    
        .financial-table th.value-column {
          width: auto;
          min-width: 160px;
          max-width: 200px;
        }
      `}</style>
      <div className="mb-4 flex justify-end items-center space-x-4">
        <ViewToggle
          currentView={viewType}
          onViewChange={setViewType}
          projectPeriodType={selectedProject?.period_type}
        />
        <button
          onClick={expandAll}
          className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md transition-colors ${
            Object.values(expandedState.sections).every(value => value) && 
            currentDataSource && currentDataSource.line_items.filter(item => item.type === 'category_header' && item.category_value).every(item => expandedState.categories[item.category_value!])
              ? 'text-white bg-blue-600 border-transparent'
              : 'text-blue-700 bg-blue-100 border-blue-300 hover:bg-blue-200'
          }`}
        >
          <ChevronDownSquare className="h-4 w-4 mr-2" />
          Expand All
        </button>
        <button
          onClick={collapseAll}
          className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md transition-colors ${
            Object.values(expandedState.sections).some(value => value) && 
            currentDataSource && !currentDataSource.line_items.filter(item => item.type === 'category_header' && item.category_value).some(item => expandedState.categories[item.category_value!])
              ? 'text-white bg-blue-600 border-transparent'
              : 'text-blue-700 bg-blue-100 border-blue-300 hover:bg-blue-200'
          }`}
        >
          <ChevronUpSquare className="h-4 w-4 mr-2" />
          Collapse All
        </button>
      </div>
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center">
        <span className="mr-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
          Active Template:
        </span>
        <span className="text-sm text-blue-800 font-medium">
          {activeTemplate.name}
        </span>
      </div>
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <div className="border border-gray-200 rounded-lg">
          <div className="max-h-screen h-128 overflow-auto">
            <div className="inline-block min-w-fit">
              <table className="financial-table divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th scope="col" className="sticky left-0 top-0 z-[110] px-6 py-3 text-left text-sm font-semibold text-black border-r border-b border-gray-200 bg-gray-50">
                      Description
                    </th>
                    <th scope="col" className="sticky top-0 z-[110] px-6 py-3 text-left text-sm font-semibold text-black border-b border-gray-200 bg-gray-50">
                      Account Number
                    </th>
                    {periods.map((period) => (
                      <th 
                        key={period} 
                        scope="col" 
                        className="sticky top-0 z-[100] px-6 py-3 text-right text-sm font-semibold text-black border-b border-gray-200 value-column bg-gray-50"
                      >
                        {viewType === 'yearly' ? `CY ${period}` : (
                          <>
                            {viewType === 'quarterly' && periodMonthRanges[period] && (
                              <div className="text-xs text-gray-500 mb-1">{periodMonthRanges[period]}</div>
                            )}
                            {getDateForPeriod(period, accounts)}
                          </>
                        )}
                      </th>
                    ))}
                    {viewType === 'yearly' && (
                      <th className="sticky top-0 z-[100] px-6 py-3 text-right text-sm font-semibold text-black border-b border-gray-200 value-column bg-gray-50">
                        {ttmInfo.range && (
                          <div className="text-xs text-gray-500 mb-1">{ttmInfo.range}</div>
                        )}
                        {ttmInfo.label}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sections.filter(section => section.categories.length > 0).map((section) => (
                    <React.Fragment key={section.title}>
                      <SectionHeader
                        title={section.title}
                        isOpen={expandedState.sections[section.title]}
                        onClick={() => toggleSection(section.title)}
                        colSpan={periods.length + (viewType === 'yearly' ? 3 : 2)}
                      />
                      {expandedState.sections[section.title] && (
                        <>
                          {section.categories.map(category => (
                            <CategoryRow
                              key={category}
                              category={category}
                              periods={periods}
                              accounts={accounts}
                              viewType={viewType}
                              allMonths={currentDataSource ? currentDataSource.periods : []}
                              isRevenue={section.isRevenue}
                              isContraRevenue={section.isContraRevenue}
                              templateCategories={activeTemplate?.categories}
                              isExpanded={!!expandedState.categories[category]}
                              onToggle={() => toggleCategory(category)}
                              selectedProject={selectedProject}
                              netRevenue={(period: string) => {
                                const netRevenueItem = transformedData.line_items.find(item => item.name === 'Net Revenue');
                                const idx = transformedData.periods.indexOf(period);
                                return netRevenueItem?.values?.[idx] ?? 0;
                              }}
                              ttmNetRevenue={viewType === 'yearly' ? (() => {
                                if (!currentDataSource || !activeTemplate) return undefined;
                                const projectPeriodType = selectedProject?.period_type || 'monthly';
                                const allMonths = currentDataSource.periods;
                                
                                // Calculate TTM Net Revenue using period-aware logic
                                const revenueCategories = ['Sales Revenue']; // Primary revenue category
                                const contraRevenueCategories = ['Sales Returns and Allowances', 'Sales Discounts']; // Contra-revenue categories
                                
                                const revenueTotal = revenueCategories.reduce((total, category) => {
                                  return total + calculateTTMForPeriodType(
                                    accounts,
                                    category,
                                    allMonths,
                                    true,  // isRevenue
                                    false, // isContraRevenue
                                    projectPeriodType
                                  );
                                }, 0);
                                
                                const contraRevenueTotal = contraRevenueCategories.reduce((total, category) => {
                                  return total + calculateTTMForPeriodType(
                                    accounts,
                                    category,
                                    allMonths,
                                    true, // isRevenue
                                    true, // isContraRevenue
                                    projectPeriodType
                                  );
                                }, 0);
                                
                                return revenueTotal + contraRevenueTotal; // contraRevenue is already negative from calculateTTMForPeriodType
                              })() : undefined}
                            />
                          ))}
                          {section.showTotal && (
                            <SectionTotal
                              title={`Total ${section.title}`}
                              periods={periods}
                              total={(period: string) => {
                                const sectionTotalItem = transformedData.line_items.find(
                                  item => item.type === 'section_total' && item.name === `Total ${section.title}`
                                );
                                const idx = transformedData.periods.indexOf(period);
                                return sectionTotalItem?.values?.[idx] ?? 0;
                              }}
                              netRevenue={(period: string) => {
                                const netRevenueItem = transformedData.line_items.find(item => item.name === 'Net Revenue');
                                const idx = transformedData.periods.indexOf(period);
                                return netRevenueItem?.values?.[idx] ?? 0;
                              }}
                              ttmTotal={viewType === 'yearly' ? (() => {
                                if (!currentDataSource) return undefined;
                                const projectPeriodType = selectedProject?.period_type || 'monthly';
                                const allMonths = currentDataSource.periods;
                                return section.categories.reduce((total, category) =>
                                  total + calculateTTMForPeriodType(
                                    accounts,
                                    category,
                                    allMonths,
                                    section.isRevenue,
                                    section.isContraRevenue,
                                    projectPeriodType
                                  ), 0);
                              })() : undefined}
                              ttmNetRevenue={viewType === 'yearly' ? (() => {
                                if (!currentDataSource || !activeTemplate) return undefined;
                                const projectPeriodType = selectedProject?.period_type || 'monthly';
                                const allMonths = currentDataSource.periods;
                                
                                // Calculate TTM Net Revenue using period-aware logic
                                const revenueCategories = ['Sales Revenue'];
                                const contraRevenueCategories = ['Sales Returns and Allowances', 'Sales Discounts'];
                                
                                const revenueTotal = revenueCategories.reduce((total, category) => {
                                  return total + calculateTTMForPeriodType(
                                    accounts,
                                    category,
                                    allMonths,
                                    true,  // isRevenue
                                    false, // isContraRevenue
                                    projectPeriodType
                                  );
                                }, 0);
                                
                                const contraRevenueTotal = contraRevenueCategories.reduce((total, category) => {
                                  return total + calculateTTMForPeriodType(
                                    accounts,
                                    category,
                                    allMonths,
                                    true, // isRevenue
                                    true, // isContraRevenue
                                    projectPeriodType
                                  );
                                }, 0);
                                
                                return revenueTotal + contraRevenueTotal;
                              })() : undefined}
                              className="bg-gray-200"
                            />
                          )}
                          {section.title === 'Sales Discounts and Returns' && (() => {
                            const netRevenueItem = transformedData.line_items.find(item => item.name === 'Net Revenue');
                            if (!netRevenueItem) return null;
                            return (
                              <SectionTotal
                                title="Net Revenue"
                                periods={periods}
                                total={(period: string) => {
                                  const idx = transformedData.periods.indexOf(period);
                                  return netRevenueItem?.values?.[idx] ?? 0;
                                }}
                                netRevenue={(period: string) => {
                                  const idx = transformedData.periods.indexOf(period);
                                  return netRevenueItem?.values?.[idx] ?? 0;
                                }}
                                ttmTotal={viewType === 'yearly' ? (() => {
                                  if (!currentDataSource || !activeTemplate) return undefined;
                                  const projectPeriodType = selectedProject?.period_type || 'monthly';
                                  const allMonths = currentDataSource.periods;
                                  // Net Revenue = Revenue + Contra Revenue
                                  const revenueCats = activeTemplate.categories.filter(cat => cat.type === 'revenue').map(cat => cat.value) || [];
                                  const contraCats = activeTemplate.categories.filter(cat => cat.type === 'contra-revenue').map(cat => cat.value) || [];
                                  const revenue = revenueCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, false, projectPeriodType), 0);
                                  const contra = contraCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, true, projectPeriodType), 0);
                                  return revenue + contra;
                                })() : undefined}
                                ttmNetRevenue={viewType === 'yearly' ? (() => {
                                  if (!currentDataSource || !activeTemplate) return undefined;
                                  const projectPeriodType = selectedProject?.period_type || 'monthly';
                                  const allMonths = currentDataSource.periods;
                                  const revenueCats = activeTemplate.categories.filter(cat => cat.type === 'revenue').map(cat => cat.value) || [];
                                  const contraCats = activeTemplate.categories.filter(cat => cat.type === 'contra-revenue').map(cat => cat.value) || [];
                                  const revenue = revenueCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, false, projectPeriodType), 0);
                                  const contra = contraCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, true, projectPeriodType), 0);
                                  return revenue + contra;
                                })() : undefined}
                                className="bg-gray-300"
                              />
                            );
                          })()}
                          {section.title === 'Cost of Goods Sold' && (() => {
                            const grossMarginItem = transformedData.line_items.find(item => item.name === 'Gross Margin');
                            if (!grossMarginItem) return null;
                            return (
                              <SectionTotal
                                title="Gross Margin"
                                periods={periods}
                                total={(period: string) => {
                                  const idx = transformedData.periods.indexOf(period);
                                  return grossMarginItem?.values?.[idx] ?? 0;
                                }}
                                netRevenue={(period: string) => {
                                  const netRevenueItem = transformedData.line_items.find(item => item.name === 'Net Revenue');
                                  const idx = transformedData.periods.indexOf(period);
                                  return netRevenueItem?.values?.[idx] ?? 0;
                                }}
                                ttmTotal={viewType === 'yearly' ? (() => {
                                  if (!currentDataSource || !activeTemplate) return undefined;
                                  const projectPeriodType = selectedProject?.period_type || 'monthly';
                                  const allMonths = currentDataSource.periods;
                                  // Gross Margin = Net Revenue - COGS
                                  const revenueCats = activeTemplate.categories.filter(cat => cat.type === 'revenue').map(cat => cat.value) || [];
                                  const contraCats = activeTemplate.categories.filter(cat => cat.type === 'contra-revenue').map(cat => cat.value) || [];
                                  const cogsCats = activeTemplate.categories.filter(cat => cat.type === 'cogs' || cat.type === 'contra-cogs').map(cat => cat.value) || [];
                                  const netRevenue = revenueCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, false, projectPeriodType), 0) + contraCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, true, projectPeriodType), 0);
                                  const cogs = cogsCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, false, false, projectPeriodType), 0);
                                  return netRevenue - cogs;
                                })() : undefined}
                                ttmNetRevenue={viewType === 'yearly' ? (() => {
                                  if (!currentDataSource || !activeTemplate) return undefined;
                                  const projectPeriodType = selectedProject?.period_type || 'monthly';
                                  const allMonths = currentDataSource.periods;
                                  const revenueCats = activeTemplate.categories.filter(cat => cat.type === 'revenue').map(cat => cat.value) || [];
                                  const contraCats = activeTemplate.categories.filter(cat => cat.type === 'contra-revenue').map(cat => cat.value) || [];
                                  const revenue = revenueCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, false, projectPeriodType), 0);
                                  const contra = contraCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, true, projectPeriodType), 0);
                                  return revenue + contra;
                                })() : undefined}
                                className="bg-gray-300"
                              />
                            );
                          })()}
                          {section.title === 'Operating Expenses' && (() => {
                            const operatingIncomeItem = transformedData.line_items.find(item => item.name === 'Operating Income');
                            if (!operatingIncomeItem) return null;
                            return (
                              <SectionTotal
                                title="Operating Income"
                                periods={periods}
                                total={(period: string) => {
                                  const idx = transformedData.periods.indexOf(period);
                                  return operatingIncomeItem?.values?.[idx] ?? 0;
                                }}
                                netRevenue={(period: string) => {
                                  const netRevenueItem = transformedData.line_items.find(item => item.name === 'Net Revenue');
                                  const idx = transformedData.periods.indexOf(period);
                                  return netRevenueItem?.values?.[idx] ?? 0;
                                }}
                                ttmTotal={viewType === 'yearly' ? (() => {
                                  if (!currentDataSource || !activeTemplate) return undefined;
                                  const projectPeriodType = selectedProject?.period_type || 'monthly';
                                  const allMonths = currentDataSource.periods;
                                  // Operating Income = Gross Margin - Operating Expenses
                                  const revenueCats = activeTemplate.categories.filter(cat => cat.type === 'revenue').map(cat => cat.value) || [];
                                  const contraCats = activeTemplate.categories.filter(cat => cat.type === 'contra-revenue').map(cat => cat.value) || [];
                                  const cogsCats = activeTemplate.categories.filter(cat => cat.type === 'cogs' || cat.type === 'contra-cogs').map(cat => cat.value) || [];
                                  const netRevenue = revenueCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, false, projectPeriodType), 0) + contraCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, true, projectPeriodType), 0);
                                  const cogs = cogsCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, false, false, projectPeriodType), 0);
                                  const grossMargin = netRevenue - cogs;
                                  const opExCats = activeTemplate.categories.filter(cat => cat.type === 'operating-expense').map(cat => cat.value) || [];
                                  const opEx = opExCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, false, false, projectPeriodType), 0);
                                  return grossMargin - opEx;
                                })() : undefined}
                                ttmNetRevenue={viewType === 'yearly' ? (() => {
                                  if (!currentDataSource || !activeTemplate) return undefined;
                                  const projectPeriodType = selectedProject?.period_type || 'monthly';
                                  const allMonths = currentDataSource.periods;
                                  const revenueCats = activeTemplate.categories.filter(cat => cat.type === 'revenue').map(cat => cat.value) || [];
                                  const contraCats = activeTemplate.categories.filter(cat => cat.type === 'contra-revenue').map(cat => cat.value) || [];
                                  const revenue = revenueCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, false, projectPeriodType), 0);
                                  const contra = contraCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, true, projectPeriodType), 0);
                                  return revenue + contra;
                                })() : undefined}
                                className="bg-gray-300"
                              />
                            );
                          })()}
                        </>
                      )}
                    </React.Fragment>
                  ))}
                  {(() => {
                    const netIncomeItem = transformedData.line_items.find(item => item.name === 'Net Income');
                    if (!netIncomeItem) return null;
                    return (
                      <tr className="bg-gray-800 text-white">
                        <td className="sticky left-0 z-10 bg-gray-800 px-6 py-4 text-sm font-semibold border-r border-gray-200">Net Income</td>
                        <td className="px-6 py-4 text-sm font-semibold text-center" />
                        {netIncomeItem.values?.map((val, idx) => (
                          <td key={idx} className="px-6 py-4 text-sm font-semibold text-right">{getPercentValue(val, idx) !== null ? `${getPercentValue(val, idx)}%` : 'n/a'}</td>
                        ))}
                        {viewType === 'yearly' && (() => {
                          if (!currentDataSource || !activeTemplate) return null;
                          const projectPeriodType = selectedProject?.period_type || 'monthly';
                          const allMonths = currentDataSource.periods;
                          
                          // Calculate TTM Net Income from current data source using period-aware logic
                          const netIncomeItemFull = currentDataSource.line_items.find(item => item.name === 'Net Income');
                          const netIncomeValues = netIncomeItemFull?.values;
                          if (!netIncomeValues) return <td className="px-6 py-4 text-sm font-semibold text-right">n/a</td>;
                          
                          // Apply period-aware lookback for Net Income
                          let lookbackPeriods: number;
                          switch (projectPeriodType) {
                            case 'quarterly':
                              lookbackPeriods = 4; // Last 4 quarters
                              break;
                            case 'annual':
                              lookbackPeriods = 1; // Last 1 year
                              break;
                            default: // monthly
                              lookbackPeriods = 12; // Last 12 months
                              break;
                          }
                          
                          const relevantNetIncomeValues = netIncomeValues.slice(-lookbackPeriods);
                          const ttmNetIncome = relevantNetIncomeValues.reduce((sum, value) => sum + (value || 0), 0);
                          
                          // Calculate TTM Net Revenue using period-aware logic
                          const revenueCategories = ['Sales Revenue'];
                          const contraRevenueCategories = ['Sales Returns and Allowances', 'Sales Discounts'];
                          
                          const revenueTotal = revenueCategories.reduce((total, category) => {
                            return total + calculateTTMForPeriodType(
                              accounts,
                              category,
                              allMonths,
                              true,  // isRevenue
                              false, // isContraRevenue
                              projectPeriodType
                            );
                          }, 0);
                          
                          const contraRevenueTotal = contraRevenueCategories.reduce((total, category) => {
                            return total + calculateTTMForPeriodType(
                              accounts,
                              category,
                              allMonths,
                              true, // isRevenue
                              true, // isContraRevenue
                              projectPeriodType
                            );
                          }, 0);
                          
                          const ttmNetRevenue = revenueTotal + contraRevenueTotal;
                          const percent = ttmNetRevenue !== 0 ? (ttmNetIncome / ttmNetRevenue) * 100 : 0;
                          return <td className="px-6 py-4 text-sm font-semibold text-right">{percent.toFixed(1)}%</td>;
                        })()}
                      </tr>
                    );
                  })()}
                  {/* EBITDA Bridge Percent */}
                  <EBITDABridgePercent
                    accounts={accounts}
                    periods={periods}
                    viewType={viewType}
                    allMonths={currentDataSource ? currentDataSource.periods : []}
                    calculateNetIncome={(period: string) => {
                      const netIncomeItem = transformedData.line_items.find(item => item.name === 'Net Income');
                      const idx = transformedData.periods.indexOf(period);
                      return netIncomeItem?.values?.[idx] ?? 0;
                    }}
                    calculateNetRevenueTotal={(period: string) => {
                      const netRevenueItem = transformedData.line_items.find(item => item.name === 'Net Revenue');
                      const idx = transformedData.periods.indexOf(period);
                      return netRevenueItem?.values?.[idx] ?? 0;
                    }}
                    ttmNetIncome={viewType === 'yearly' ? (() => {
                      if (!currentDataSource) return undefined;
                      const projectPeriodType = selectedProject?.period_type || 'monthly';
                      const netIncomeItem = currentDataSource.line_items.find(item => item.name === 'Net Income');
                      const netIncomeValues = netIncomeItem?.values;
                      if (!netIncomeValues) return 0;
                      
                      // Apply period-aware lookback
                      let lookbackPeriods: number;
                      switch (projectPeriodType) {
                        case 'quarterly':
                          lookbackPeriods = 4; // Last 4 quarters
                          break;
                        case 'annual':
                          lookbackPeriods = 1; // Last 1 year
                          break;
                        default: // monthly
                          lookbackPeriods = 12; // Last 12 months
                          break;
                      }
                      
                      const relevantValues = netIncomeValues.slice(-lookbackPeriods);
                      return relevantValues.reduce((sum, value) => sum + (value || 0), 0);
                    })() : undefined}
                    templateCategories={activeTemplate?.categories}
                    selectedProject={selectedProject}
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedDetailedIncomeStatementPercent;